"use client";

import { useEffect, useMemo, useRef, useState, type FC } from "react";
import {
   Dialog,
   DialogContent,
   DialogDescription,
   DialogHeader,
   DialogTitle,
   Loading
} from "@/app/components/ui";
import { ShareInviteForm } from "@/app/components/share/ShareInviteForm";
import { ShareAccessSection } from "@/app/components/share/ShareAccessSection";
import { ShareDialogFooter } from "@/app/components/share/ShareDialogFooter";
import { ShareSettingsView } from "@/app/components/share/ShareSettingsView";
import { useAuth, useToast } from "@/app/hooks";
import { useTranslations } from "next-intl";
import { api_url } from "@/app/api";
import { ChatHistory, ShareAccess, ShareInvite } from "@/types";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import { useStateHistoryChatPage } from "@/app/store/chat";
import { METHOD_TYPE } from "@/app/lib/enums";
import { HTTP_STATUS, SHARE_ACCESS, SHARE_ROUTER_SEGMENT } from "@/app/lib/constants";

interface CreatePublicLinkChatHistoryProps {
   item: ChatHistory;
   isOpen: boolean;
   setOpen: (show: boolean) => void;
   actionsDisabled?: boolean;
   onShareStateChange?: (item: Partial<ChatHistory>) => void;
}

type ShareAction = "create" | "update" | "unshare" | "invite" | "remove" | "access";

type ShareState = Pick<
   ChatHistory,
   "isShared" | "shareKey" | "lastSharedAt" | "lastMessageAt" | "updatedAt"
> & {
   shareAccess: ShareAccess | null;
   shareInvites: ShareInvite[];
};

const COPY_RESET_DELAY_MS = 1_000;
const SAME_SAVE_TIMESTAMP_TOLERANCE_MS = 1_000;

function mapChatHistoryToShareState(item?: Partial<ChatHistory>): ShareState {
   return {
      isShared: item?.isShared ?? false,
      shareKey: item?.shareKey || "",
      lastSharedAt: item?.lastSharedAt,
      lastMessageAt: item?.lastMessageAt,
      updatedAt: item?.updatedAt || "",
      shareAccess: item?.shareAccess ?? null,
      shareInvites: item?.shareInvites ?? []
   };
}

function parseDate(value?: string) {
   if (!value) return null;

   const date = new Date(value);
   if (Number.isNaN(date.getTime())) return null;

   return date;
}

async function requestShare<T = Partial<ChatHistory>>(
   url: string,
   init: { method: METHOD_TYPE; body: Record<string, unknown> }
): Promise<T | null> {
   const response = await fetchWithAuth(url, {
      method: init.method,
      body: JSON.stringify(init.body)
   });
   if (!response.ok) throw response;

   const { data } = await response.json();
   return data ?? null;
}

export const CreatePublicLinkChatHistory: FC<CreatePublicLinkChatHistoryProps> = ({
   item,
   isOpen,
   setOpen,
   actionsDisabled = false,
   onShareStateChange
}) => {
   const { toast } = useToast();
   const { user } = useAuth();
   const translate = useTranslations("Home");
   const { historyItem } = useStateHistoryChatPage();
   const [freshItem, setFreshItem] = useState<Partial<ChatHistory> | null>(null);
   const [loadingFreshState, setLoadingFreshState] = useState(false);
   const resolvedItem = useMemo<Partial<ChatHistory>>(() => {
      if (freshItem?.id === item.id) return freshItem;
      if (!item?.id || historyItem?.id !== item.id) return item;

      return {
         ...item,
         ...historyItem,
         isShared: historyItem.isShared ?? item.isShared,
         shareKey: historyItem.shareKey || item.shareKey,
         lastSharedAt: historyItem.lastSharedAt ?? item.lastSharedAt,
         lastMessageAt: historyItem.lastMessageAt ?? item.lastMessageAt,
         updatedAt: historyItem.updatedAt ?? item.updatedAt
      };
   }, [freshItem, historyItem, item]);
   const [origin, setOrigin] = useState("");
   const [shareState, setShareState] = useState<ShareState>(() =>
      mapChatHistoryToShareState(resolvedItem)
   );
   const [view, setView] = useState<"main" | "settings">("main");
   const [isCopying, setIsCopying] = useState(false);
   const [loadingAction, setLoadingAction] = useState<ShareAction | null>(null);
   const [removingEmail, setRemovingEmail] = useState<string | null>(null);
   const shareMutationVersionRef = useRef(0);

   useEffect(() => {
      setOrigin(window.location.origin);
   }, []);

   useEffect(() => {
      if (!isOpen) return;
      shareMutationVersionRef.current = 0;
      setView("main");
   }, [isOpen, item?.id]);

   useEffect(() => {
      if (!isOpen || loadingAction || shareMutationVersionRef.current > 0) return;
      setShareState(mapChatHistoryToShareState(resolvedItem));
   }, [
      isOpen,
      loadingAction,
      resolvedItem?.id,
      resolvedItem?.isShared,
      resolvedItem?.lastMessageAt,
      resolvedItem?.lastSharedAt,
      resolvedItem?.shareKey,
      resolvedItem?.updatedAt,
      resolvedItem?.shareAccess,
      resolvedItem?.shareInvites
   ]);

   useEffect(() => {
      if (!isOpen || !item?.id) return;

      let isCancelled = false;
      const requestMutationVersion = shareMutationVersionRef.current;
      setFreshItem(null);
      setLoadingFreshState(true);

      const loadFreshShareState = async () => {
         try {
            const url = new URL(
               api_url.chat_history_share_invites_url,
               window.location.origin
            );
            url.searchParams.set("chatHistoryId", item.id);
            const response = await fetchWithAuth(
               url.toString(),
               { method: METHOD_TYPE.GET },
               "no-store"
            );
            const { data: nextItem } = response.ok
               ? await response.json()
               : { data: null };
            if (
               !isCancelled &&
               requestMutationVersion === shareMutationVersionRef.current &&
               nextItem?.id === item.id
            ) {
               setFreshItem(nextItem);
               setShareState(mapChatHistoryToShareState(nextItem));
            }
         } catch {
            if (!isCancelled) setFreshItem(null);
         } finally {
            if (!isCancelled) setLoadingFreshState(false);
         }
      };

      void loadFreshShareState();

      return () => {
         isCancelled = true;
      };
   }, [isOpen, item?.id]);

   const isShareActive = Boolean(shareState.isShared && shareState.shareKey);
   const access = shareState.shareAccess ?? SHARE_ACCESS.INVITED;
   const toShareUrl = (shareKey?: string) =>
      origin && shareKey ? `${origin}/${SHARE_ROUTER_SEGMENT}/${shareKey}` : "";
   const owner = {
      name: user?.username || "",
      email: user?.email || ""
   };

   const isUpdateLink = useMemo(() => {
      if (!isShareActive) return false;

      const lastSharedDate = parseDate(shareState.lastSharedAt);
      const lastMessageDate = parseDate(shareState.lastMessageAt || shareState.updatedAt);

      if (!lastSharedDate || !lastMessageDate) return false;

      return (
         lastMessageDate.getTime() - lastSharedDate.getTime() >
         SAME_SAVE_TIMESTAMP_TOLERANCE_MS
      );
   }, [
      isShareActive,
      shareState.lastMessageAt,
      shareState.lastSharedAt,
      shareState.updatedAt
   ]);

   const areActionsDisabled =
      actionsDisabled || loadingFreshState || Boolean(loadingAction);

   const applyShareState = (data: Partial<ChatHistory>, action: ShareAction) => {
      setShareState((current) => {
         const shareKey = data.shareKey ?? current.shareKey;
         const isShared =
            action === "unshare" ? false : (data.isShared ?? Boolean(shareKey));

         return {
            isShared,
            shareKey: isShared ? shareKey || "" : "",
            lastSharedAt: data.lastSharedAt ?? current.lastSharedAt,
            lastMessageAt: data.lastMessageAt ?? current.lastMessageAt,
            updatedAt: data.updatedAt || current.updatedAt,
            shareAccess: isShared ? (data.shareAccess ?? current.shareAccess) : null,
            shareInvites: isShared ? (data.shareInvites ?? current.shareInvites) : []
         };
      });
   };

   const showErrorToast = (errorKey: string, descriptionKey = "tryAgain") => {
      toast({
         title: translate("error", { error: translate(errorKey) }),
         description: translate(descriptionKey),
         variant: "destructive"
      });
   };

   const showSuccessToast = (successKey: string) => {
      toast({
         title: translate("success"),
         description: translate(successKey),
         variant: "success"
      });
   };

   const copyLink = async (url: string) => {
      if (!url) return;

      setIsCopying(true);
      try {
         await navigator.clipboard.writeText(url);
         showSuccessToast("linkCopied");
      } catch {
         showErrorToast("failedToCopy");
      } finally {
         setTimeout(() => setIsCopying(false), COPY_RESET_DELAY_MS);
      }
   };

   const runShareAction = async (
      { action, errorKey }: { action: ShareAction; errorKey: string },
      task: (chatHistoryId: string) => Promise<Partial<ChatHistory> | null>
   ) => {
      if (!resolvedItem?.id) {
         showErrorToast(errorKey);
         return null;
      }

      shareMutationVersionRef.current += 1;
      setLoadingAction(action);
      try {
         const data = await task(resolvedItem.id);
         if (!data) {
            showErrorToast(errorKey);
            return null;
         }

         applyShareState(data, action);
         onShareStateChange?.(data);
         return data;
      } catch (error) {
         const isRateLimited =
            error instanceof Response && error.status === HTTP_STATUS.TOO_MANY_REQUESTS;
         showErrorToast(errorKey, isRateLimited ? "shareRateLimited" : "tryAgain");
         return null;
      } finally {
         setLoadingAction(null);
      }
   };

   const handleCopyLink = async () => {
      if (areActionsDisabled) return;
      if (isShareActive) return copyLink(toShareUrl(shareState.shareKey));

      const data = await runShareAction(
         { action: "create", errorKey: "failedToCreate" },
         (chatHistoryId) =>
            requestShare(api_url.chat_history_share_url, {
               method: METHOD_TYPE.POST,
               body: { chatHistoryId }
            })
      );
      if (data?.shareKey) await copyLink(toShareUrl(data.shareKey));
   };

   const handleUpdateLink = async () => {
      if (!isShareActive || areActionsDisabled) return;

      const data = await runShareAction(
         { action: "update", errorKey: "failedToUpdateLink" },
         (chatHistoryId) =>
            requestShare(api_url.chat_history_share_url, {
               method: METHOD_TYPE.PUT,
               body: { chatHistoryId }
            })
      );
      if (!data) return;

      showSuccessToast("linkUpdated");
      if (data.shareKey) await copyLink(toShareUrl(data.shareKey));
   };

   const handleStopSharing = async () => {
      if (!isShareActive || areActionsDisabled) return;

      const data = await runShareAction(
         { action: "unshare", errorKey: "failedToUnshare" },
         (chatHistoryId) =>
            requestShare(api_url.chat_history_share_url, {
               method: METHOD_TYPE.DELETE,
               body: { chatHistoryId }
            })
      );
      if (!data) return;

      showSuccessToast("shareSetPrivate");
      setView("main");
   };

   const handleAccessChange = async (nextAccess: ShareAccess) => {
      if (areActionsDisabled || nextAccess === access) return;

      const data = await runShareAction(
         { action: "access", errorKey: "failedToUpdateAccess" },
         async (chatHistoryId) => {
            if (!isShareActive) {
               const created = await requestShare(api_url.chat_history_share_url, {
                  method: METHOD_TYPE.POST,
                  body: { chatHistoryId }
               });
               if (!created) return null;

               applyShareState(created, "create");
               onShareStateChange?.(created);
            }

            return requestShare(api_url.chat_history_share_access_url, {
               method: METHOD_TYPE.PUT,
               body: { chatHistoryId, access: nextAccess }
            });
         }
      );
      if (data) showSuccessToast("shareAccessUpdated");
   };

   const handleInvite = async (emails: string[]) => {
      let failedEmails: string[] = [];
      const data = await runShareAction(
         { action: "invite", errorKey: "failedToInvite" },
         async (chatHistoryId) => {
            const result = await requestShare<{
               chatHistory: Partial<ChatHistory>;
               failedEmails: string[];
            }>(api_url.chat_history_share_invites_url, {
               method: METHOD_TYPE.POST,
               body: { chatHistoryId, emails }
            });
            failedEmails = result?.failedEmails ?? [];
            return result?.chatHistory ?? null;
         }
      );
      if (!data) return false;

      const hasFailedEmails = failedEmails.length > 0;
      toast({
         title: translate("shareInvitesSent", { count: emails.length }),
         description: hasFailedEmails
            ? translate("shareInviteEmailNotSent", { emails: failedEmails.join(", ") })
            : translate("shareInviteCheckSpam"),
         variant: hasFailedEmails ? "warning" : "success"
      });
      return true;
   };

   const handleRemoveInvite = async (email: string) => {
      if (areActionsDisabled) return;

      setRemovingEmail(email);
      const data = await runShareAction(
         { action: "remove", errorKey: "failedToRemoveInvite" },
         (chatHistoryId) =>
            requestShare(api_url.chat_history_share_invites_url, {
               method: METHOD_TYPE.DELETE,
               body: { chatHistoryId, email }
            })
      );
      setRemovingEmail(null);
      if (data) showSuccessToast("shareInviteRemoved");
   };

   return (
      <Dialog open={isOpen} onOpenChange={setOpen}>
         <DialogContent
            className="max-h-[90vh] max-w-[600px] grid-cols-[minmax(0,1fr)] gap-5 overflow-y-auto p-6 sm:p-7"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
         >
            {view === "settings" ? (
               <ShareSettingsView
                  access={access}
                  isShared={isShareActive}
                  disabled={areActionsDisabled}
                  isStopping={loadingAction === "unshare"}
                  onBack={() => setView("main")}
                  onAccessChange={(nextAccess) => void handleAccessChange(nextAccess)}
                  onStopSharing={() => void handleStopSharing()}
               />
            ) : (
               <>
                  <DialogHeader className="space-y-1 text-left">
                     <DialogTitle className="text-xl font-semibold text-foreground">
                        {translate("shareDialogTitle")}
                     </DialogTitle>
                     <DialogDescription>
                        {translate("shareDialogDescription")}
                     </DialogDescription>
                  </DialogHeader>

                  {actionsDisabled && (
                     <p className="rounded-xl border border-primary/20 bg-primary/10 px-3 py-2 text-sm font-medium text-primary">
                        {translate("shareActionsPendingResponse")}
                     </p>
                  )}
                  {loadingFreshState && (
                     <div className="flex items-center gap-2 rounded-xl border border-border bg-muted px-3 py-2 text-sm font-medium text-muted-foreground">
                        <Loading size="sm" />
                        <span>{translate("loading")}</span>
                     </div>
                  )}

                  <ShareInviteForm
                     ownerEmail={owner.email}
                     disabled={areActionsDisabled}
                     isInviting={loadingAction === "invite"}
                     onInvite={handleInvite}
                  />

                  <ShareAccessSection
                     access={access}
                     owner={owner}
                     invites={shareState.shareInvites}
                     disabled={areActionsDisabled}
                     removingEmail={removingEmail}
                     onOpenSettings={() => setView("settings")}
                     onRemove={(email) => void handleRemoveInvite(email)}
                  />

                  <ShareDialogFooter
                     hasNewerMessages={isUpdateLink}
                     disabled={areActionsDisabled}
                     isUpdating={loadingAction === "update"}
                     isCreating={loadingAction === "create"}
                     isCopying={isCopying}
                     onUpdateLink={() => void handleUpdateLink()}
                     onCopyLink={() => void handleCopyLink()}
                  />
               </>
            )}
         </DialogContent>
      </Dialog>
   );
};
